import { Loader2 } from "lucide-react";
import { useState } from "react";
import type { AdminApi } from "@/api";
import {
  Badge,
  Banner,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import type { CallSiteCell, CallSiteModel, PromptCallSitesView } from "@/types/callSites";
import { errorText } from "@/components/model-catalog/errorCatalog";

/**
 * The call sites that read one prompt, with the model each one sends in dev and prod (MODELALL U14).
 *
 * A list and links only (user decision 2026-10-27: call sites and prompts are separate screens): a site's
 * models are changed on the call-site screen (`/call-sites?key=`), never here.
 */

/** An attempt's model as saved, in words: never filled in with a default. */
export function modelLabel(model: CallSiteModel): string {
  if (typeof model.ref === "string") return model.ref;
  if (model.run === true) return "실행이 정함";
  if (typeof model.setting === "string") return `설정 ${model.setting}`;
  if (typeof model.base_child_setting === "string") return `child 설정 ${model.base_child_setting}`;
  if (typeof model.inherit_site === "string") return `상속: ${model.inherit_site}`;
  return JSON.stringify(model);
}

/** Where a site is edited: the call-site screen, opened on that site. */
export function callSiteHref(key: string) {
  return `/call-sites?key=${encodeURIComponent(key)}`;
}

function Models({ cell }: { cell: CallSiteCell | null }) {
  if (!cell) return <span className="text-muted-foreground">버전 없음</span>;
  if (cell.models.length === 0) {
    return (
      <span className="grid gap-0.5">
        <span className="text-sm text-muted-foreground">{cell.selection === "disabled" ? "사용 안 함" : "모델 단계 없음"}</span>
        <small className="text-xs text-muted-foreground">v{cell.version}</small>
      </span>
    );
  }
  return (
    <span className="grid gap-0.5">
      {cell.models.map((m, i) => (
        <span key={m.index} className="text-sm">
          <span className="text-muted-foreground">{i === 0 ? "" : "예비 "}</span><code>{modelLabel(m.model)}</code>
        </span>
      ))}
      <small className="text-xs text-muted-foreground">v{cell.version}</small>
    </span>
  );
}

export function PromptCallSitesPanel({ api, target }: { api: AdminApi; target: string }) {
  const [view, setView] = useState<PromptCallSitesView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      setView(await api.promptCallSites(target));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="grid gap-3 pt-3" data-testid={`call-sites-${target}`}>
      <div className="flex items-center gap-2">
        <strong className="text-sm">이 프롬프트를 쓰는 호출 지점</strong>
        <Button size="sm" variant="outline" disabled={loading} onClick={load}>
          {loading && <Loader2 className="animate-spin" />}{view ? "다시 읽기" : "보기"}
        </Button>
      </div>
      {error && <Banner tone="error">호출 지점을 읽지 못했습니다: {error}</Banner>}
      {view && !view.linked && (
        <p className="m-0 text-sm text-muted-foreground">이 프롬프트를 쓰는 호출 지점이 없습니다.</p>
      )}
      {view?.shared && (
        <Banner tone="warn">
          여러 지점이 함께 쓰는 프롬프트입니다. 이 프롬프트를 고치면 아래 {view.sites.length}곳이 모두 영향을 받습니다. 지점마다 모델은 다를 수 있습니다.
        </Banner>
      )}
      {view && view.sites.length > 0 && (
        <>
          <p className="m-0 text-xs text-muted-foreground">모델과 예비 순서는 호출 지점 화면에서 바꿉니다.</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>호출 지점</TableHead>
                <TableHead>dev 모델</TableHead>
                <TableHead>prod 모델</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {view.sites.map((site) => (
                <TableRow key={site.key}>
                  <TableCell>
                    <span className="grid gap-0.5">
                      {site.title && <span className="text-sm">{site.title}</span>}
                      <code className={site.title ? "text-xs text-muted-foreground" : undefined}>{site.key}</code>
                    </span>
                    {site.scoring && <Badge size="compact" className="ml-1.5">채점</Badge>}
                  </TableCell>
                  <TableCell><Models cell={site.dev} /></TableCell>
                  <TableCell>
                    {view.prod_hidden ? <span className="text-muted-foreground">prod 권한 없음</span> : <Models cell={site.prod} />}
                  </TableCell>
                  <TableCell>
                    {site.assignable && (
                      <a className="text-sm text-primary underline-offset-2 hover:underline" href={callSiteHref(site.key)}>
                        호출 지점 화면에서 보기
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
}
